import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { onSheetsChange } from '../css-sheet.ts';

/**
 * Um quadro (`iframe`) com a largura do aparelho, para a prévia do estúdio (ADR 0133, parte 3).
 * Dentro dele as peças decidem o desenho pela largura do quadro, como num celular ou tablet de
 * verdade (as regras `md:`, `lg:` do Tailwind olham a janela), e a letra maior muda a raiz dele.
 * As folhas de estilo da página são espelhadas lá dentro, inclusive as que o tema troca depois;
 * o conteúdo entra por portal, então continua no mesmo React (contextos e eventos).
 */
export function IsolatedFrame({
  width,
  zoom,
  rootAttrs,
  onHeight,
  children,
}: {
  width: number;
  zoom: number;
  /** Marcas do `<html>` do quadro (data-theme, data-text, data-contrast). */
  rootAttrs: Record<string, string>;
  onHeight: (h: number) => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [body, setBody] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const frame = ref.current;
    const doc = frame?.contentDocument;
    if (!frame || !doc) return;
    doc.open();
    doc.write('<!doctype html><html><head><meta charset="utf-8"></head><body></body></html>');
    doc.close();
    const win = frame.contentWindow as (Window & typeof globalThis) | null;
    // As folhas da página (o styles.css) e as construídas (tema, acabamento, prévias), copiadas.
    const mirrorTags = () => {
      doc.head.querySelectorAll('[data-mirror]').forEach((n) => n.remove());
      document.head.querySelectorAll('style, link[rel="stylesheet"]').forEach((n) => {
        const copy = n.cloneNode(true) as HTMLElement;
        copy.setAttribute('data-mirror', '');
        doc.head.appendChild(copy);
      });
    };
    const mirrorSheets = () => {
      if (!win) return;
      doc.adoptedStyleSheets = document.adoptedStyleSheets.map((s) => {
        const copy = new win.CSSStyleSheet();
        copy.replaceSync(Array.from(s.cssRules, (r) => r.cssText).join('\n'));
        return copy;
      });
    };
    mirrorTags();
    mirrorSheets();
    const mo = new MutationObserver(mirrorTags);
    mo.observe(document.head, { childList: true, subtree: true, characterData: true });
    const off = onSheetsChange(mirrorSheets);
    doc.body.style.margin = '0';
    doc.body.style.background = 'transparent';
    setBody(doc.body);
    return () => {
      mo.disconnect();
      off();
    };
  }, []);

  useEffect(() => {
    const root = ref.current?.contentDocument?.documentElement;
    if (!root) return;
    for (const [k, v] of Object.entries(rootAttrs)) root.setAttribute(k, v);
  }, [rootAttrs, body]);

  useEffect(() => {
    if (!body) return;
    const ro = new ResizeObserver(() => onHeight(body.scrollHeight));
    ro.observe(body);
    return () => ro.disconnect();
  }, [body, onHeight]);

  return (
    <>
      <iframe
        ref={ref}
        title="preview"
        tabIndex={-1}
        className="block border-0"
        style={{ width, height: '100%', zoom }}
      />
      {body && createPortal(children, body)}
    </>
  );
}
